import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

export interface SyncLog {
	id: string;
	entityType: string;
	direction: "inbound" | "outbound" | "bidirectional";
	status: "pending" | "in_progress" | "synced" | "failed" | "manual_review";
	operation: string | null;
	durationMs: number | null;
	errorMessage: string | null;
	response: Record<string, unknown> | null;
	createdAt: string;
	updatedAt: string;
	retries: number;
}

export interface SyncLogsFilters {
	entity?: string | undefined;
	status?: string | undefined;
	limit?: number | undefined;
	offset?: number | undefined;
}

export interface SyncLogsResponse {
	items: SyncLog[];
	total: number;
}

const KEY = "sync-logs";

export function useSyncLogs(filters: SyncLogsFilters) {
	const queryParams = new URLSearchParams();
	if (filters.entity) queryParams.set("entity", filters.entity);
	if (filters.status) queryParams.set("status", filters.status);
	if (filters.limit !== undefined) queryParams.set("limit", String(filters.limit));
	if (filters.offset !== undefined) queryParams.set("offset", String(filters.offset));

	return useQuery({
		queryKey: [KEY, filters],
		queryFn: () => api.get<SyncLogsResponse>(`/api/sync/logs?${queryParams.toString()}`),
		refetchInterval: (query) => (query.state.error ? false : 5000),
	});
}

export function useRetrySyncLog() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<SyncLog>(`/api/sync/logs/${id}/retry`, {}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [KEY] });
		},
	});
}
